/**
 * Feature-badges — small "New / Live / Beta" pill renderer for nav
 * surfaces (sidebar, mobile bottom-nav, future menu).
 *
 * Founder direction 2026-05-02: when a feature lands, mark it with
 * a "New" badge so users + founder can spot what changed. Lasts a
 * day by default; configurable per-badge.
 *
 * Static-date model — no backend state. The badge expires when
 * `Date.now() >= expiresAt`. Component re-renders pick up the new
 * state on next mount. No server round-trip; bundle-size cost is
 * one helper + a few bytes of metadata per nav item.
 *
 * To add a new badge: edit the consumer's nav list (`SiteSidebar`,
 * `MobileBottomNav`, etc.), set `badge: { kind: 'new', expiresAt:
 * '2026-05-03T23:59:00Z' }` (or use `daysFromNow(1)` shorthand).
 * To remove: delete the field. To rotate: bump expiresAt.
 *
 * **Why not a backend service?** Founder's spec is "lasts a day for
 * now" — adding a Postgres row + admin UI for an ephemeral marker
 * is over-build. Static dates ship in 1 commit; revisit if we hit
 * the cadence where founder rotates badges weekly.
 */

export type BadgeKind = "new" | "live" | "beta" | "soon";

export interface FeatureBadge {
  kind: BadgeKind;
  /**
   * ISO-8601 string. Badge hides when `Date.now() >= expiresAt`.
   * Omit to show indefinitely (use sparingly — `live` and `beta`
   * are the only common indefinite cases).
   */
  expiresAt?: string;
}

/**
 * Returns true when the badge is still active. Indefinite badges
 * (no `expiresAt`) are always active. Past-dated badges are not.
 */
export function isBadgeActive(badge: FeatureBadge | undefined): boolean {
  if (!badge) return false;
  if (!badge.expiresAt) return true;
  const expiry = Date.parse(badge.expiresAt);
  if (Number.isNaN(expiry)) return false;
  return Date.now() < expiry;
}

/**
 * Convenience for setting expiry N days from a fixed launch date.
 * Pass the launch date as ISO so the duration is deterministic
 * regardless of when the user loads the page; the `daysFromNow`
 * variant below is for tests / dev only.
 */
export function expiresAfter(launchedAt: string, days: number): string {
  const start = Date.parse(launchedAt);
  if (Number.isNaN(start)) {
    throw new Error(`expiresAfter: invalid launchedAt ${launchedAt}`);
  }
  return new Date(start + days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Founder-facing label per badge kind. i18n catalog can override
 * via the `featureBadges.<kind>` keys; helper below resolves with
 * the en fallback when i18n hasn't hydrated.
 */
export const DEFAULT_BADGE_LABEL: Record<BadgeKind, string> = {
  new: "New",
  live: "Live",
  beta: "Beta",
  soon: "Soon",
};

/**
 * Resolves a badge label from a translator function (e.g. `t` from
 * `useTranslation`). Pass the `t` reference; the helper handles
 * the i18n key lookup + fallback to the locale-neutral default.
 *
 * Catalog keys: `featureBadges.{new|live|beta|soon}`. All 5 locales
 * (en, pt, en-CA, fr-CA, es-MX) carry these.
 */
export function resolveBadgeLabel(
  kind: BadgeKind,
  t?: (key: string, fallback?: string) => string,
): string {
  if (!t) return DEFAULT_BADGE_LABEL[kind];
  return t(`featureBadges.${kind}`, DEFAULT_BADGE_LABEL[kind]);
}

/**
 * Color tokens per kind. Pinned to the design system's brand /
 * semantic palette. The Tailwind classes here resolve against
 * the existing light + dark theme tokens.
 */
export const BADGE_CLASSNAMES: Record<BadgeKind, string> = {
  // Bright accent — draws attention without screaming.
  new: "bg-[var(--color-brand)] text-[var(--color-brand-contrast)]",
  // Muted — established surface, just signaling "this is active".
  live: "border border-[var(--color-border)] text-[var(--color-text2)]",
  // Warm yellow accent for in-progress surfaces.
  beta: "bg-[#a26a00] text-white",
  // Dim grey for forthcoming features.
  soon: "border border-[var(--color-border)] text-[var(--color-text3)]",
};
